import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useJobStore } from "../../store/jobStore";
import { toast } from "react-toastify";

interface ApplyJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: any;
  lang: "id" | "en";
}

export default function ApplyJobModal({
  isOpen,
  onClose,
  job,
  lang,
}: ApplyJobModalProps) {
  const navigate = useNavigate();

  const authRaw = localStorage.getItem("auth-storage");
  const auth = authRaw ? JSON.parse(authRaw)?.state : null;

  const isLoggedIn = !!auth?.accessToken;
  const isCandidate = auth?.type === "candidate";
  const isProfileCompleted = auth?.dataProfile === true;

  /** ✅ ambil dari store DI ATAS */
  const { fetchApplyPreview, applyPreview, submitApply } = useJobStore();
  const [submitting, setSubmitting] = useState(false);


  /** ✅ fetch GET preview saat modal dibuka */
  useEffect(() => {
    if (isOpen && job?.id && isLoggedIn && isCandidate) {
      fetchApplyPreview(job.id);
    }
  }, [isOpen, job?.id, isLoggedIn, isCandidate, fetchApplyPreview]);

  if (!isOpen) return null;

  /** ✅ mapping sesuai response */
  const avatar = applyPreview?.photo ?? "https://placehold.co/200";
  const userName = applyPreview?.name ?? "-";
  const userPhone = applyPreview?.phone ?? "-";
  const userEmail = applyPreview?.email ?? "-";
  const userCvName = applyPreview?.resume_name ?? "-";
  const userCvUrl = applyPreview?.resume ?? "#";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* OVERLAY */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white w-full max-w-sm md:max-w-lg rounded-xl shadow-lg p-6 z-10">

        {/* ================= APPLY FLOW ================= */}
        {!isLoggedIn ? (
          /* ================= CASE 3: BELUM LOGIN ================= */
          <>
            <h3 className="text-xl font-semibold text-[#123b7c] mb-2">
              {lang === "id" ? "Silakan Login" : "Please Login"}
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              {lang === "id"
                ? "Anda harus login terlebih dahulu untuk melamar pekerjaan."
                : "You need to login before applying for a job."}
            </p>

            <button
              onClick={() => navigate(`/${lang}/login`)}
              className="bg-[#123b7c] text-white w-full py-2 rounded-full font-medium"
            >
              Login
            </button>
          </>
        ) : !isCandidate ? (
          /* ================= CASE 4: LOGIN TAPI BUKAN KANDIDAT ================= */
          <>
            <h3 className="text-xl font-semibold text-[#123b7c] mb-2">
              {lang === "id" ? "Silakan Login" : "Please Login"}
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              {lang === "id"
                ? "Anda harus login terlebih dahulu sebagai kandidat untuk melamar pekerjaan."
                : "You need to login as candidate before applying for a job."}
            </p>

            <button
              onClick={() => navigate(`/${lang}/login`)}
              className="bg-[#123b7c] text-white w-full py-2 rounded-full font-medium"
            >
              Login
            </button>
          </>
        ) : !isProfileCompleted ? (
          /* ================= CASE 2: PROFILE BELUM LENGKAP ================= */
          <>
            <h3 className="text-xl font-semibold text-[#123b7c] mb-2">
              {lang === "id" ? "Lengkapi Profil Anda" : "Complete Your Profile"}
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              {lang === "id"
                ? "Silakan lengkapi profil Anda sebelum melamar pekerjaan."
                : "Please complete your profile before applying for a job."}
            </p>

            <button
              onClick={() => navigate(`/${lang}/user/user-profile`)}
              className="bg-[#123b7c] text-white w-full py-2 rounded-full font-medium"
            >
              {lang === "id" ? "Lengkapi Profil" : "Complete Profile"}
            </button>
          </>
        ) : (
          /* ================= CASE 1: AUTH OK ================= */
          <>
            <h3 className="text-xl font-semibold text-[#123b7c] mb-4">
              {lang === "id"
                ? "Konfirmasi Lamar Pekerjaan"
                : "Confirm Job Application"}
            </h3>

            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-4">
                <img className="w-24 h-24 rounded-full object-cover" src={avatar} alt="" />
                <div>
                  <p>
                    <strong>{lang === "id" ? "Nama" : "Name"}:</strong> {userName}
                  </p>
                  <p>
                    <strong>Email:</strong> {userEmail}
                  </p>
                  <p>
                    <strong>Phone:</strong> {userPhone}
                  </p>
                </div>
              </div>

              <p>
                <strong>{lang === "id" ? "Posisi Pekerjaan" : "Job Position"}:</strong>{" "}
                {job?.title}
              </p>
              <p>
                <strong>{lang === "id" ? "Lokasi Pekerjaan" : "Job Location"}:</strong>{" "}
                {job?.location}
              </p>

              {/* CV INFO */}
              <div className="bg-gray-50 border rounded-lg p-3">
                <p className="font-medium mb-1">
                  {lang === "id" ? "Resume yang digunakan" : "Resume used"}
                </p>

                <a
                  href={userCvUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 underline flex items-center gap-2"
                >
                  📎 View CV
                </a>

                <button
                  className="text-xs text-blue-600 mt-2 hover:underline"
                  onClick={() => navigate(`/${lang}/user/user-profile`)}
                >
                  {lang === "id"
                    ? "Ganti Resume di Profil"
                    : "Change Resume in Profile"}
                </button>
              </div>
            </div>

            {/* ACTIONS */}
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full border text-gray-600 hover:bg-gray-100"
              >
                {lang === "id" ? "Batal" : "Cancel"}
              </button>

              <button
                onClick={async () => {
                  if (!job?.id || submitting) return;

                  setSubmitting(true);

                  const result = await submitApply(job.id);

                  setSubmitting(false);

                  if (result.success) {
                    toast.success(
                      result.message ||
                      (lang === "id"
                        ? "Lamaran berhasil dikirim 🎉"
                        : "Application submitted successfully 🎉")
                    );
                    onClose();
                  } else {
                    toast.error(
                      result.message ||
                      (lang === "id"
                        ? "Gagal mengirim lamaran"
                        : "Failed to submit application")
                    );
                  }
                }}

                disabled={submitting}
                className="px-5 py-2 rounded-full bg-[#123b7c] text-white font-medium hover:bg-[#0e2f66] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? (lang === "id" ? "Mengirim..." : "Submitting...")
                  : (lang === "id" ? "Kirim Lamaran" : "Submit Application")}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
